import React from 'react';
import { type ColumnDef } from '@oysterjs/ui/data-table';
import { Badge } from '@oysterjs/ui/badge';

import type { CustomFormListFieldsFragment } from '~/api/graphql';
import { CustomFormStatus } from '~/api/graphql';

import { customFormCreatorLabel } from '~/api/data/forms/custom-forms-filters';
import {
  CreatedByCell,
  CreatedDateCell,
  EditLinkCell,
  TitleCell,
  selectionColumn
} from '~/components/list/library-table-cells';

export type CustomFormSortKey = 'title' | 'user' | 'status' | 'created';

const EDIT_BASE = '/app/library/forms';

const STATUS_COLOR: Record<CustomFormStatus, 'green' | 'yellow' | 'red' | 'neutral'> = {
  [CustomFormStatus.Ready]: 'green',
  [CustomFormStatus.Queued]: 'yellow',
  [CustomFormStatus.Processing]: 'yellow',
  [CustomFormStatus.Failed]: 'red'
};

const DISABLED_EDIT_TITLE =
  'This form cannot be edited while it is processing. Try again when the status is Ready or Failed.';

/**
 * A custom form is editable in the library exactly when its server-side
 * ingestion has settled (Ready / Failed). While Queued or Processing the
 * row is shown but not interactive.
 */
export function isCustomFormEditable(form: CustomFormListFieldsFragment): boolean {
  return form.status !== CustomFormStatus.Queued && form.status !== CustomFormStatus.Processing;
}

/** rowHref helper: returns the edit URL only for editable forms. */
export function customFormRowHref(form: CustomFormListFieldsFragment): string | undefined {
  return isCustomFormEditable(form) ? `${EDIT_BASE}/${form.id}` : undefined;
}

export function getCustomFormColumns(opts: {
  selectedSet: ReadonlySet<string>;
  setSelectedForms: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: CustomFormListFieldsFragment[];
  userNameById: (id: string) => string;
  /**
   * Whether the caller may change these rows. False drops the bulk-selection
   * column and turns the row link from "Edit" into "View".
   */
  canEdit?: boolean;
}): ColumnDef<CustomFormListFieldsFragment, CustomFormSortKey, never>[] {
  const { selectedSet, setSelectedForms, filtered, userNameById } = opts;
  return [
    ...(opts.canEdit === false
      ? []
      : [
          selectionColumn<CustomFormListFieldsFragment, CustomFormSortKey>({
            selectedSet,
            setSelected: setSelectedForms,
            filtered,
            pluralLabel: 'forms'
          })
        ]),
    {
      id: 'title',
      header: 'Title',
      sortKey: 'title',
      renderCell: (form) => <TitleCell title={form.title} visibility={form.visibility} />
    },
    {
      id: 'user',
      header: 'Created by',
      sortKey: 'user',
      renderCell: (form) => <CreatedByCell name={customFormCreatorLabel(form, userNameById)} />
    },
    {
      id: 'status',
      header: 'Status',
      sortKey: 'status',
      renderCell: (form) => (
        <Badge color={STATUS_COLOR[form.status] ?? 'neutral'}>{form.status}</Badge>
      )
    },
    {
      id: 'created',
      header: 'Created',
      sortKey: 'created',
      renderCell: (form) => <CreatedDateCell value={form.createdAt} />
    },
    {
      id: 'actions',
      header: 'Actions',
      noHref: true,
      cellClassName: 'w-0 py-1.5 px-3',
      renderCell: (form) => (
        <EditLinkCell
          canEdit={opts.canEdit}
          href={`${EDIT_BASE}/${form.id}`}
          disabledTitle={isCustomFormEditable(form) ? undefined : DISABLED_EDIT_TITLE}
        />
      )
    }
  ];
}

export function customFormSortFn(
  data: readonly CustomFormListFieldsFragment[],
  sortKey: CustomFormSortKey,
  direction: 'asc' | 'desc',
  userNameById: (id: string) => string
): CustomFormListFieldsFragment[] {
  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    }
    if (sortKey === 'user') {
      return customFormCreatorLabel(a, userNameById).localeCompare(
        customFormCreatorLabel(b, userNameById)
      );
    }
    if (sortKey === 'status') {
      return a.status.localeCompare(b.status);
    }
    const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return aDate - bDate;
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
}
